/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { List, ListItem, Collapse, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import '../../../rsmCoreComponents/components/FiltersGroup/FiltersGroup.css'
import StyledButton from '../../DocumentRequests/components/StyledButton/StyledButton'
import {
  getPaymentSelectedFilters,
  PaymentSelectedFiltersConstants,
} from '../../../store/invoices/paymentSelectedFiltersSlice'
import InvoicesFilterCategoryContainer from './InvoicesFilterCategoryContainer'
import CheckBoxFilterItem from './CheckBoxFilterItem'
import { MoreLess, processMoreLess } from './CheckBoxFilters.service'

const FilterList = styled(List)({
  border: 'none',
})

const FilterItem = styled(ListItem)(() => ({
  display: 'flex',
  alignItems: 'middle',
  padding: '0 0',
}))

const style = {
  button: {
    height: 56,
    color: 'secondary.main',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0 6px',
  },
  text: {
    color: 'secondary.main',
    fontWeight: 'medium',
    fontSize: '1.125rem',
    fontFamily: 'Prelo-Medium, sans-serif',
  },
}

interface PaymentFiltersCheckBoxesProps {
  filterCategory: string
  filterOptions: string[]
  clearCategoryFilter: (s: string) => void
  changeFilterAndSearch: (s: string) => void
  visibleItemsCount: number
}

const PaymentFiltersCheckBoxes = ({
  filterCategory,
  filterOptions,
  clearCategoryFilter,
  changeFilterAndSearch,
  visibleItemsCount,
}: PaymentFiltersCheckBoxesProps) => {
  const selectedFilters = useSelector(getPaymentSelectedFilters)
  const { t } = useTranslation()
  const [moreLessData, setmoreLessData] = useState<MoreLess>({
    showMoreLess: false,
    alwaysVisibleItems: [],
    moreItems: [],
    showMore: false,
  })

  const isFilterChecked = useCallback(
    (category: string, option: string): boolean => {
      if (category === PaymentSelectedFiltersConstants.paymentStatus)
        return selectedFilters.paymentStatus.indexOf(option) >= 0
      return false
    },
    [selectedFilters, PaymentSelectedFiltersConstants],
  )

  const handleMoreLess = useCallback(() => {
    let moreless: MoreLess = {
      showMoreLess: true,
      alwaysVisibleItems: filterOptions,
      moreItems: [],
      showMore: false,
    }

    if (!moreLessData.showMore)
      moreless = processMoreLess(filterOptions, visibleItemsCount)
    setmoreLessData(moreless)
  }, [filterOptions, visibleItemsCount, moreLessData, setmoreLessData])

  useEffect(() => {
    const moreless = processMoreLess(filterOptions, visibleItemsCount)
    setmoreLessData(moreless)
  }, [filterOptions, visibleItemsCount])

  const getCheckBoxFilterItems = (items: string[]) =>
    items.map((option: string) => (
      <CheckBoxFilterItem
        key={option}
        filterCategory={filterCategory}
        option={option}
        isFilterChecked={isFilterChecked}
        changeFilterAndSearch={changeFilterAndSearch}
        testId={`Btn_Payment_Filter_${option}`}
      />
    ))

  return (
    <InvoicesFilterCategoryContainer
      filterCategory={filterCategory}
      clearCategoryFilter={clearCategoryFilter}>
      <FilterList>
        {getCheckBoxFilterItems(moreLessData.alwaysVisibleItems)}
      </FilterList>
      <Collapse in={!moreLessData.showMore} timeout="auto" unmountOnExit>
        {getCheckBoxFilterItems(moreLessData.moreItems)}
      </Collapse>
      {moreLessData.showMoreLess && (
        <FilterItem>
          <StyledButton
            sx={style.button}
            data-testid={
              moreLessData.showMore
                ? `Btn_Payment_SeeMore${filterCategory}`
                : `Btn_Payment_SeeLess${filterCategory}`
            }
            onClick={handleMoreLess}
            aria-live="polite">
            <Typography sx={style.text}>
              {moreLessData.showMore
                ? t('Filters.CollapsedToggle.SeeMore')
                : t('Filters.CollapsedToggle.SeeLess')}
            </Typography>
          </StyledButton>
        </FilterItem>
      )}
    </InvoicesFilterCategoryContainer>
  )
}

export default PaymentFiltersCheckBoxes
